import { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { styles } from '../../../styles/themes/style';
import { AppRoutes } from '../../../interfaces/store/appStore';
import { useAppActions } from '../../../stores/application/useAppActions';
import { useSignalsRequest } from '../../../services/apis/signals/useSignalsRequest';
import {
  ISignalAccount, ISignalsAccess, ISignalSettingsResponse, ISignalSource, LinkedInReadKind,
} from '../../../interfaces/models/signals';
import SignalSourcesPanel from '../components/signals/SignalSourcesPanel';
import SignalSettingsPanel from '../components/signals/SignalSettingsPanel';
import SignalLeadsPanel from '../components/signals/SignalLeadsPanel';
import { Icon, Muted, PrimaryButton } from '../components/signals/signalsUi';

/**
 * Signals — the warm lead radar. LeadDelta watches what each cloud-connected LinkedIn
 * account can see (profile viewers, engagement on posts, job changes in its network)
 * and lists the people it finds, for the team to review and send into campaigns.
 *
 * The reading itself runs in the ld-gcp-signal-collector Cloud Function, a few times a
 * day during working hours; this page manages what it watches and what happens next.
 */
const SignalsSection: FunctionComponent = () => {
  const api = useSignalsRequest();
  const apiRef = useRef(api);
  apiRef.current = api;
  const navigate = useNavigate();
  const { setToast } = useAppActions();

  const [access, setAccess] = useState<ISignalsAccess | null>(null);
  const [accounts, setAccounts] = useState<ISignalAccount[]>([]);
  const [caps, setCaps] = useState<Record<LinkedInReadKind, number> | null>(null);
  const [sources, setSources] = useState<ISignalSource[]>([]);
  const [settings, setSettings] = useState<ISignalSettingsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async () => {
    const accessRes = await apiRef.current.getAccess();
    const nextAccess: ISignalsAccess | null = accessRes?.success ? accessRes.data : null;
    setAccess(nextAccess);
    if (!nextAccess?.allowed) {
      setLoading(false);
      return;
    }
    const [accountsRes, sourcesRes, settingsRes] = await Promise.all([
      apiRef.current.getAccounts(),
      apiRef.current.getSources(),
      apiRef.current.getSettings(),
    ]);
    if (settingsRes?.success) setSettings(settingsRes.data);
    if (accountsRes?.success) {
      setAccounts(accountsRes.data?.accounts ?? []);
      setCaps(accountsRes.data?.dailyReadCaps ?? null);
    } else {
      setToast({ type: 'error', message: accountsRes?.message || 'Could not load your LinkedIn accounts.' });
    }
    if (sourcesRes?.success) setSources(sourcesRes.data?.sources ?? []);
    setLoading(false);
    // setToast is stable for the lifetime of the store.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { void load(); }, [load]);

  const onSourcesChanged = () => {
    void load();
    setRefreshKey(k => k + 1);
  };

  return (
    <Container>
      <PageHeader>
        <div>
          <PageTitle>Signals</PageTitle>
          <Muted>People on LinkedIn who just showed interest — the right person, at the right time. Checked a few times a day in the background.</Muted>
        </div>
      </PageHeader>

      {access && !access.allowed && (
        <Locked>
          <Icon name="radar" size={36} />
          <strong>{access.reason === 'disabled' ? 'Signals are taking a short break' : 'Signals are part of the Growth plan'}</strong>
          <Muted>{access.message}</Muted>
          {access.requiresUpgrade && (
            <PrimaryButton type="button" onClick={() => navigate(AppRoutes.BILLING)}>See plans</PrimaryButton>
          )}
        </Locked>
      )}

      {access?.allowed && (
        <>
          <SignalSourcesPanel
            access={access}
            accounts={accounts}
            dailyReadCaps={caps}
            sources={sources}
            loading={loading}
            onChanged={onSourcesChanged}
          />
          {settings && <SignalSettingsPanel data={settings} onSaved={onSourcesChanged} />}
          <SignalLeadsPanel refreshKey={refreshKey} settings={settings?.settings ?? null} />
        </>
      )}
    </Container>
  );
};

export default SignalsSection;

const Container = styled.div`
  width: 100%; padding: 0 24px 32px;
  display: flex; flex-direction: column; gap: 16px;
  height: 100%; overflow-y: auto;
`;

const PageHeader = styled.div`
  padding-top: 16px;
  display: flex; align-items: flex-start; justify-content: space-between; gap: 16px;
`;

const PageTitle = styled.h1`
  margin: 0 0 2px;
  font-family: NunitoSans; font-size: 20px; font-weight: 700; line-height: 28px;
  color: ${styles.colors.black600};
`;

const Locked = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 10px; text-align: center;
  padding: 56px 24px; background: ${styles.colors.white};
  border: 1px dashed ${styles.colors.black200}; border-radius: 18px;
  color: ${styles.colors.black400};
  strong { font-family: NunitoSans; font-size: 16px; color: ${styles.colors.black600}; }
  svg { opacity: 0.4; }
`;
